import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  IconButton,
  Button,
  Stack,
  Chip,
  Fade,
  styled
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import CachedIcon from '@mui/icons-material/Cached';

import { API_ENDPOINT } from '../../../configurations/global.config';
import CreateEntityDrawer from '../../common/create-entity-drawer/create-entity-drawer';
import CustomersListing from './customers-listing/customers-listing';
import { useSnackBar } from '../../../contexts/snackbar';
import CreateCustomerForm from '../../common/create-customer-form/create-customer-form';
import MerchantBulkCreateCustomers from './bulk-import/customer-bulk-importer';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { IAddressResolutionTask, TaskState } from '../../../interfaces/common/async_tasks';


interface AITaskCardProps {
  status: TaskState;
}

const AITaskCard = styled(Paper)<AITaskCardProps>(({ theme, status }) => ({
  padding: theme.spacing(2),
  width: '100%',
  maxWidth: 600,
  background: theme.palette.background.paper,
  borderLeft: '6px solid',
  borderColor: theme.palette[status === 'FAILED' 
    ? 'error' 
    : status === 'COMPLETED' 
    ? 'success' 
    : 'primary'].main,
  position: 'relative',
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
}));

function MerchantCustomers() {
  const [pendingTasks, setPendingTasks] = useState<IAddressResolutionTask[]>([]);
  const { showMessage } = useSnackBar();
  const { config } = useContext(AuthContext);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get<IAddressResolutionTask[]>(
        `${API_ENDPOINT}/v1/jobs/address-resolution/`,
        config,
      );
      setPendingTasks(response.data);

      const stillPending = response.data.some(
        (task) => task.status !== 'COMPLETED' && task.status !== 'FAILED'
      );

      if (!stillPending && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      } else if (stillPending && !pollingRef.current) {
        pollingRef.current = setInterval(fetchTasks, 1000);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      showMessage('Failed to fetch tasks', 'error');
    }
  }, [config, showMessage]);

  const dismissTask = useCallback(async (taskId: string) => {
    try {
      await axios.post(
        `${API_ENDPOINT}/v1/jobs/dismiss/`,
        { task_ids: [taskId] },
        config
      );
      setPendingTasks(prev => prev.filter(task => task.task_id !== taskId));
      showMessage('Task dismissed');
    } catch (error) {
      console.error('Failed to dismiss task:', error);
      showMessage('Failed to dismiss task', 'error');
    }
  }, [config, showMessage]);

  const dismissAllCompleted = useCallback(async () => {
    const completedTaskIds = pendingTasks
      .filter(task => task.status === 'COMPLETED' || task.status === 'FAILED')
      .map(task => task.task_id);

    if (completedTaskIds.length === 0) {
      showMessage('No completed tasks to dismiss');
      return;
    }

    try {
      await axios.post(
        `${API_ENDPOINT}/v1/jobs/dismiss/`,
        { task_ids: completedTaskIds },
        config
      );
      setPendingTasks(prev => prev.filter(
        task => !completedTaskIds.includes(task.task_id)
      ));
      showMessage(`Dismissed ${completedTaskIds.length} tasks`);
    } catch (error) {
      console.error('Failed to dismiss tasks:', error);
      showMessage('Failed to dismiss tasks', 'error');
    }
  }, [pendingTasks, config, showMessage]);

  useEffect(() => {
    fetchTasks();
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchTasks]);

  const hasCompletedTasks = pendingTasks.some(
    task => task.status === 'COMPLETED' || task.status === 'FAILED'
  );

  return (
    <div className="content-container">
      <Box>
        <Typography variant="h3" component="h3" sx={{ textAlign: 'center', mt: 3, mb: 3 }}>
          Customers
        </Typography>

        {pendingTasks.length > 0 && (
          <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Stack direction="row" spacing={2} sx={{ width: '100%', maxWidth: 600, mb: 2 }}>
              {hasCompletedTasks && (
                <Button
                  variant="outlined"
                  startIcon={<DoneAllIcon />}
                  onClick={dismissAllCompleted}
                  sx={{ ml: 'auto' }}
                >
                  Dismiss All Completed
                </Button>
              )}
            </Stack>

            {pendingTasks.map((task) => (
              <Fade in={true} key={task.task_id}>
                <AITaskCard elevation={3} status={task.status}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <Chip
                        icon={<PublicIcon fontSize="small" />}
                        label="AI GEO RESOLVER"
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 600, mr: 1 }}
                      />
                      {task.status === 'STARTED' && (
                        <CachedIcon 
                          sx={{ 
                            fontSize: 16,
                            animation: 'spin 1.5s linear infinite',
                            '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } }
                          }} 
                        />
                      )}
                    </Box>
                    <IconButton onClick={() => dismissTask(task.task_id)} size="small">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                    <LocationOnIcon 
                      color={task.status === 'COMPLETED' ? 'success' : 'primary'} 
                      sx={{ fontSize: 40, mr: 2 }} 
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {task.status === 'COMPLETED'
                          ? `Resolved ${Object.keys(task.result_summary).length} locations`
                          : task.status === 'FAILED'
                          ? 'Resolution failed'
                          : 'Analyzing address patterns...'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Task ID: {task.task_id.slice(0, 8)}... • {new Date(task.created_at).toLocaleTimeString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={task.progress}
                      color={
                        task.status === 'FAILED'
                          ? 'error'
                          : task.status === 'COMPLETED'
                          ? 'success'
                          : 'primary'
                      }
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {task.status === 'STARTED' ? 'Processing...' : task.status}
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {task.progress}%
                      </Typography>
                    </Box>
                  </Box>
                </AITaskCard>
              </Fade>
            ))}
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid xs={4}></Grid>
          <Grid xs={4}></Grid>
          <Grid xs={4} sx={{ textAlign: 'right' }}>
            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
              <MerchantBulkCreateCustomers onSuccess={() => fetchTasks()} />
              <CreateEntityDrawer>
                <CreateCustomerForm />
              </CreateEntityDrawer>
            </div>
          </Grid>
          <Grid xs={12}>
            <CustomersListing />
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}

export default MerchantCustomers;